import type {
  SocialLoginRequest,
  SocialProvider,
} from '@/entities/auth/api/auth.api';

/**
 * M-01 SNS 로그인 — 네 제공자 모두 리다이렉트로 인가하고 /auth/callback/{provider}에서 받는다.
 * 제공자 SDK 스크립트를 쓰지 않아 디자인한 버튼을 그대로 쓸 수 있다.
 * 서버는 제공자 토큰을 검증만 하므로, 프론트가 토큰까지 받아서 넘긴다.
 *
 * - kakao: 인가 코드(query) → 프론트가 REST API 키로 kauth 토큰 교환 → id_token
 *   (카카오 앱의 Client Secret이 꺼져 있어야 브라우저에서 교환할 수 있다)
 * - naver: implicit grant(response_type=token) → fragment의 access_token
 * - google: OIDC response_type=id_token (fragment)
 * - apple: response_mode=fragment — scope를 비워야 fragment를 쓸 수 있다 (이름 · 이메일 불필요)
 *
 * redirect_uri는 각 제공자 콘솔에 등록한 값(/auth/callback/{provider})만 맞으면 되고 서버로 보내지 않는다.
 * 프론트에 넣는 값은 공개 식별자뿐이다.
 */

export const SOCIAL_PROVIDERS: readonly SocialProvider[] = [
  'kakao',
  'naver',
  'google',
  'apple',
];

export const SOCIAL_PROVIDER_LABEL: Record<SocialProvider, string> = {
  kakao: '카카오',
  naver: '네이버',
  google: 'Google',
  apple: 'Apple',
};

export const isSocialProvider = (v: unknown): v is SocialProvider =>
  SOCIAL_PROVIDERS.includes(v as SocialProvider);

const getClientId = (provider: SocialProvider) =>
  ({
    kakao: import.meta.env.VITE_KAKAO_REST_API_KEY,
    naver: import.meta.env.VITE_NAVER_CLIENT_ID,
    google: import.meta.env.VITE_GOOGLE_CLIENT_ID,
    apple: import.meta.env.VITE_APPLE_SERVICES_ID,
  })[provider];

const PENDING_KEY = 'rudalink-web:social-login';

/** 인가 요청을 보낼 때 저장해 두고 콜백에서 대조하는 값 */
interface PendingSocialLogin {
  provider: SocialProvider;
  state: string;
  /** 원문. Apple만 인가 요청에 SHA-256 hex를 넣는다 */
  nonce: string;
  /** 로그인 후 돌아갈 내부 경로 */
  returnTo?: string;
}

/** 제공자 콘솔에 호스트별로 https://{호스트}/auth/callback/{provider} 를 등록한다 */
export const getRedirectUri = (provider: SocialProvider) =>
  `${window.location.origin}/auth/callback/${provider}`;

const randomToken = () => crypto.randomUUID().replace(/-/g, '');

const sha256Hex = async (text: string) => {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(text),
  );
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

const buildAuthorizeUrl = async (
  provider: SocialProvider,
  clientId: string,
  { state, nonce }: PendingSocialLogin,
) => {
  const redirectUri = getRedirectUri(provider);
  switch (provider) {
    case 'kakao':
      // scope=openid가 없으면 ID token이 발급되지 않아 서버가 401을 준다
      return `https://kauth.kakao.com/oauth/authorize?${new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid',
        nonce,
        state,
      })}`;
    case 'naver':
      // 네이버 로그인 JS SDK와 같은 implicit grant — access token이 fragment로 온다
      return `https://nid.naver.com/oauth2.0/authorize?${new URLSearchParams({
        response_type: 'token',
        client_id: clientId,
        redirect_uri: redirectUri,
        state,
      })}`;
    case 'google':
      return `https://accounts.google.com/o/oauth2/v2/auth?${new URLSearchParams(
        {
          client_id: clientId,
          redirect_uri: redirectUri,
          response_type: 'id_token',
          scope: 'openid',
          nonce,
          state,
          prompt: 'select_account',
        },
      )}`;
    case 'apple':
      return `https://appleid.apple.com/auth/authorize?${new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'code id_token',
        response_mode: 'fragment',
        state,
        nonce: await sha256Hex(nonce),
      })}`;
  }
};

/** 제공자 인가 화면으로 이동한다. 클라이언트 ID가 설정되지 않았으면 던진다 */
export const startSocialLogin = async (
  provider: SocialProvider,
  returnTo?: string,
) => {
  const clientId = getClientId(provider);
  if (!clientId) {
    throw new Error(`${provider} 클라이언트 ID가 설정되지 않았습니다`);
  }
  const pending: PendingSocialLogin = {
    provider,
    state: randomToken(),
    nonce: randomToken(),
    returnTo,
  };
  const url = await buildAuthorizeUrl(provider, clientId, pending);
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  window.location.assign(url);
};

export type SocialCallbackResult =
  | { ok: true; body: SocialLoginRequest; returnTo?: string }
  | { ok: false; returnTo?: string };

/**
 * 카카오 인가 코드 → id_token. 인가 요청과 같은 REST API 키 · redirect_uri를 쓴다.
 * scope=openid로 인가했으므로 응답에 id_token이 있다. 실패하면 null
 */
const exchangeKakaoCode = async (code: string) => {
  try {
    const res = await fetch('https://kauth.kakao.com/oauth/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: getClientId('kakao') ?? '',
        redirect_uri: getRedirectUri('kakao'),
        code,
      }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { id_token?: string };
    return data.id_token ?? null;
  } catch {
    return null;
  }
};

/**
 * 콜백 주소의 query · fragment를 읽어 서버 요청 본문을 만든다.
 * 저장해 둔 요청은 한 번만 쓰도록 읽자마자 지운다.
 * 사용자 취소 · 제공자 오류 · state 불일치 · 카카오 토큰 교환 실패는 모두 실패(X1)다.
 */
export const readSocialCallback = async (
  provider: SocialProvider,
): Promise<SocialCallbackResult> => {
  const raw = sessionStorage.getItem(PENDING_KEY);
  sessionStorage.removeItem(PENDING_KEY);
  let pending: PendingSocialLogin | null = null;
  try {
    pending = raw ? (JSON.parse(raw) as PendingSocialLogin) : null;
  } catch {
    pending = null;
  }

  // 카카오만 query, 나머지는 fragment로 돌아온다
  const params = new URLSearchParams(
    provider === 'kakao'
      ? window.location.search
      : window.location.hash.slice(1),
  );

  if (
    !pending ||
    pending.provider !== provider ||
    params.has('error') ||
    params.get('state') !== pending.state
  ) {
    return { ok: false, returnTo: pending?.returnTo };
  }
  const { returnTo, nonce } = pending;
  const fail: SocialCallbackResult = { ok: false, returnTo };

  if (provider === 'naver') {
    const accessToken = params.get('access_token');
    return accessToken ? { ok: true, body: { accessToken }, returnTo } : fail;
  }

  const idToken =
    provider === 'kakao'
      ? await exchangeKakaoCode(params.get('code') ?? '')
      : params.get('id_token');
  // 서버에는 nonce 원문을 보낸다 (Apple 인가 요청에만 해시를 넣었다)
  return idToken ? { ok: true, body: { idToken, nonce }, returnTo } : fail;
};
